/**
 * POS Product Detail Drawer
 *
 * Drawer displaying detailed information about a POS product.
 * Shows product info, sync status, and recipe linking.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@/components/ui';
import { Badge } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { usePosProduct } from '../hooks';
import {
  RecipeAssociationCard,
  AssociateRecipeModal,
} from '@/features/recipes/components';
import {
  useRecipeByPosProduct,
  useAddRecipePosProduct,
  useRemoveRecipePosProduct,
} from '@/features/recipes/hooks';
import type { PosProductType, SyncStatus } from '@bstock/shared';
import {
  Package,
  Settings,
  Tag,
  Hash,
  Euro,
  Calendar,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  DollarSign,
} from 'lucide-react';
import { MarginCard } from './MarginIndicator';

interface PosProductDrawerProps {
  productId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

// Type badge variants
const typeBadgeVariants: Record<PosProductType, { label: string; variant: 'primary' | 'neutral' }> = {
  PRODUCT: { label: 'Produit', variant: 'primary' },
  OPTION: { label: 'Option', variant: 'neutral' },
};

// Sync status badge variants
const syncStatusVariants: Record<SyncStatus, { label: string; variant: 'success' | 'warning' | 'danger'; icon: typeof CheckCircle2 }> = {
  SYNCED: { label: 'Synchronise', variant: 'success', icon: CheckCircle2 },
  PENDING: { label: 'En attente', variant: 'warning', icon: Clock },
  ERROR: { label: 'Erreur', variant: 'danger', icon: AlertCircle },
};

export function PosProductDrawer({ productId, open, onOpenChange, canEdit = true, canDelete: _canDelete = true }: PosProductDrawerProps) {
  const { data: product, isLoading, error } = usePosProduct(productId ?? undefined);
  const { data: recipe, isLoading: isRecipeLoading } = useRecipeByPosProduct(productId ?? undefined);
  const addPosProduct = useAddRecipePosProduct();
  const removePosProduct = useRemoveRecipePosProduct();
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const navigate = useNavigate();

  // Modal state
  const [isAssociateRecipeOpen, setIsAssociateRecipeOpen] = useState(false);

  // Recipe actions
  const handleCreateRecipe = useCallback(() => {
    // Navigate to recipe creation page with product info
    if (product) {
      navigate(`/recettes/create?posProductId=${product.id}&defaultName=${encodeURIComponent(product.name)}`);
    }
  }, [navigate, product]);

  const handleAssociateExisting = useCallback(() => {
    setIsAssociateRecipeOpen(true);
  }, []);

  const handleViewRecipe = useCallback(() => {
    if (recipe?.id) {
      navigate(`/recettes/${recipe.id}`);
    }
  }, [navigate, recipe?.id]);

  const handleDissociate = useCallback(() => {
    if (recipe && productId) {
      removePosProduct.mutate({ recipeId: recipe.id, posProductId: productId });
    }
  }, [recipe, productId, removePosProduct]);

  const handleRecipeSelected = useCallback((recipeId: string) => {
    if (productId) {
      addPosProduct.mutate(
        { recipeId, posProductId: productId },
        {
          onSuccess: () => {
            setIsAssociateRecipeOpen(false);
          },
        }
      );
    }
  }, [productId, addPosProduct]);

  // Loading state
  if (isLoading) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Chargement..."
        size="large"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      </Drawer>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Erreur"
        size="large"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-neutral-600">
            Impossible de charger les details du produit.
          </p>
        </div>
      </Drawer>
    );
  }

  const typeInfo = typeBadgeVariants[product.type as PosProductType];
  const syncInfo = syncStatusVariants[product.syncStatus as SyncStatus];
  const SyncIcon = syncInfo.icon;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={product.name}
      description={`ID POS: ${product.posId}`}
      size="large"
    >
      <div className="space-y-6">
        {/* Badges row */}
        <div className="flex flex-wrap gap-2">
          <Badge variant={typeInfo.variant}>
            {product.type === 'PRODUCT' ? (
              <Package className="mr-1 h-3 w-3" />
            ) : (
              <Settings className="mr-1 h-3 w-3" />
            )}
            {typeInfo.label}
          </Badge>
          <Badge variant={syncInfo.variant}>
            <SyncIcon className="mr-1 h-3 w-3" />
            {syncInfo.label}
          </Badge>
        </div>

        {/* Cost Summary - only show if there's a recipe with cost */}
        {recipe && (
          <div className="grid grid-cols-2 gap-4">
            {/* Cost Card */}
            <div className="rounded-lg bg-neutral-50 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white text-neutral-600">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-neutral-600 uppercase tracking-wide">Cout theorique</p>
                  {recipe.theoreticalCost !== null ? (
                    <p className="text-2xl font-bold text-dark-500">
                      {formatCentimes(recipe.theoreticalCost, currency)}
                    </p>
                  ) : (
                    <p className="text-lg font-semibold text-neutral-400">Non calcule</p>
                  )}
                </div>
              </div>
            </div>

            {/* Margin Card */}
            <MarginCard
              sellingPrice={product.sellPrice}
              theoreticalCost={recipe.theoreticalCost}
              currency={currency}
            />
          </div>
        )}

        {/* Product Information Card */}
        <div className="rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-200 px-4 py-3">
            <h3 className="font-medium text-dark-500">Informations produit</h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Name */}
            <div className="flex items-start gap-3">
              <Package className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">Nom</p>
                <p className="font-medium text-dark-500 truncate">{product.name}</p>
              </div>
            </div>

            {/* SKU */}
            <div className="flex items-start gap-3">
              <Hash className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">SKU</p>
                <p className="font-medium text-dark-500">{product.sku || '-'}</p>
              </div>
            </div>

            {/* Category */}
            <div className="flex items-start gap-3">
              <Tag className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">Categorie POS</p>
                <p className="font-medium text-dark-500">{product.posCategory || '-'}</p>
              </div>
            </div>

            {/* Sell Price */}
            <div className="flex items-start gap-3">
              <Euro className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">Prix de vente</p>
                <p className="font-medium text-dark-500">{formatCentimes(product.sellPrice, currency)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sync Information Card */}
        <div className="rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-200 px-4 py-3">
            <h3 className="font-medium text-dark-500">Synchronisation</h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Last Sync */}
            <div className="flex items-start gap-3">
              <RefreshCw className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">Derniere synchronisation</p>
                <p className="font-medium text-dark-500">{formatDateShort(product.lastSyncAt)}</p>
              </div>
            </div>

            {/* Imported At */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-neutral-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-500">Importe le</p>
                <p className="font-medium text-dark-500">{formatDateShort(product.importedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recipe Association */}
        <RecipeAssociationCard
          posProductId={product.id}
          posProductName={product.name}
          recipe={recipe ?? null}
          currency={currency}
          isLoading={isRecipeLoading}
          onCreateRecipe={canEdit ? handleCreateRecipe : undefined}
          onAssociateExisting={canEdit ? handleAssociateExisting : undefined}
          onViewRecipe={handleViewRecipe}
          onDissociate={canEdit ? handleDissociate : undefined}
        />
      </div>

      {/* Associate Recipe Modal */}
      {canEdit && (
        <AssociateRecipeModal
          isOpen={isAssociateRecipeOpen}
          onClose={() => setIsAssociateRecipeOpen(false)}
          onSelectRecipe={handleRecipeSelected}
          currentRecipeId={recipe?.id}
        />
      )}
    </Drawer>
  );
}
